import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type TourStatus = 'idle' | 'active' | 'skipped' | 'completed';

interface TourState {
  status: TourStatus;
  currentStepIndex: number;
  /** Bumped when reset() is called so listeners can re-trigger. */
  resetNonce: number;
  /**
   * True once the tour has auto-started on the user's first visit. Persisted so
   * the intro tour is shown only on that first visit and stays disabled on every
   * subsequent visit (LIL-355) — even if the user abandoned it mid-way. Manual
   * restarts via the TopBar (`reset()`) ignore this flag.
   */
  hasAutoStarted: boolean;
}

interface TourActions {
  /**
   * Activate the tour from the current step. No-op once status is `completed`
   * — use `reset()` to re-run from step 0 (e.g. from the TopBar help button).
   */
  start: () => void;
  /**
   * Auto-start the intro tour on the user's first visit. No-op if it has already
   * auto-started before (persisted) or the tour is already finished/active, so
   * subsequent visits never re-show it. Sets `hasAutoStarted` on first run.
   */
  autoStart: () => void;
  next: (totalSteps: number) => void;
  prev: () => void;
  goTo: (index: number) => void;
  skip: () => void;
  finish: () => void;
  /** Resume from a previously skipped state at the last visited step. */
  resume: () => void;
  /** Clear progress and re-run from step 0. */
  reset: () => void;
}

export const useTourStore = create<TourState & TourActions>()(
  devtools(
    persist(
      (set, get) => ({
        status: 'idle',
        currentStepIndex: 0,
        resetNonce: 0,
        hasAutoStarted: false,

        start: () =>
          set(
            state =>
              state.status === 'completed'
                ? state
                : { status: 'active', currentStepIndex: state.currentStepIndex },
            false,
            'tour/start'
          ),

        autoStart: () =>
          set(
            state =>
              state.hasAutoStarted || state.status !== 'idle'
                ? { hasAutoStarted: true }
                : { status: 'active', currentStepIndex: 0, hasAutoStarted: true },
            false,
            'tour/autoStart'
          ),

        next: totalSteps =>
          set(
            state => {
              const next = state.currentStepIndex + 1;
              if (next >= totalSteps) {
                return { status: 'completed', currentStepIndex: totalSteps - 1 };
              }
              return { currentStepIndex: next };
            },
            false,
            'tour/next'
          ),

        prev: () =>
          set(
            state => ({ currentStepIndex: Math.max(0, state.currentStepIndex - 1) }),
            false,
            'tour/prev'
          ),

        goTo: index => set({ currentStepIndex: Math.max(0, index) }, false, 'tour/goTo'),

        skip: () => set({ status: 'skipped' }, false, 'tour/skip'),

        finish: () => set({ status: 'completed' }, false, 'tour/finish'),

        resume: () => {
          const { status } = get();
          if (status === 'skipped' || status === 'idle') {
            set({ status: 'active' }, false, 'tour/resume');
          }
        },

        reset: () =>
          set(
            state => ({
              status: 'active',
              currentStepIndex: 0,
              resetNonce: state.resetNonce + 1,
            }),
            false,
            'tour/reset'
          ),
      }),
      {
        name: 'lili-tour-storage',
        partialize: state => ({
          status: state.status,
          currentStepIndex: state.currentStepIndex,
          hasAutoStarted: state.hasAutoStarted,
        }),
        onRehydrateStorage: () => state => {
          // The intro tour is a first-visit-only experience (LIL-355). If a
          // previous visit left it mid-flight (`active`) but it had already
          // auto-started, don't resurrect it on this visit — only an explicit
          // manual restart (TopBar `reset()`) should re-show it.
          if (state && state.hasAutoStarted && state.status === 'active') {
            state.status = 'skipped';
          }
        },
      }
    ),
    {
      name: 'TourStore',
      enabled: import.meta.env.DEV,
    }
  )
);
